import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "../constants";
import { useLanguage } from "../contexts/LanguageContext";

export const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const navLinks = [
    { id: "preise", label: t.nav.prices },
    { id: "ueber-uns", label: t.nav.about },
    { id: "galerie", label: t.nav.gallery },
    { id: "kontakt", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled || open
          ? "bg-[#0A0D14]/90 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between gap-4">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center min-w-0"
          data-testid="header-logo-button"
        >
          <Logo size="md" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="font-heading uppercase tracking-wider text-sm text-white/80 hover:text-white transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="hidden sm:flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1"
            data-testid="language-switcher"
          >
            <button
              onClick={() => setLanguage("de")}
              className={`text-xs font-heading uppercase tracking-wider px-3 py-1 rounded-full transition-all ${
                language === "de"
                  ? "bg-[#E63946] text-white"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid="lang-de-btn"
            >
              DE
            </button>
            <button
              onClick={() => setLanguage("ku")}
              className={`text-xs font-heading uppercase tracking-wider px-3 py-1 rounded-full transition-all ${
                language === "ku"
                  ? "bg-[#E63946] text-white"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid="lang-ku-btn"
            >
              KU
            </button>
          </div>

          <a
            href={BUSINESS.phoneHref}
            className="hidden md:inline-flex items-center gap-2 text-sm font-heading uppercase tracking-wider text-white/80 hover:text-white transition-colors"
            data-testid="header-phone-link"
          >
            <Phone className="w-4 h-4" />
            <span>{BUSINESS.phone}</span>
          </a>

          <button
            className="lg:hidden text-white p-2 -mr-2"
            onClick={() => setOpen((p) => !p)}
            data-testid="mobile-menu-toggle"
            aria-label="Menü"
            aria-expanded={open}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#0A0D14] border-t border-white/10"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="text-left font-heading uppercase tracking-wider text-lg text-white/90 hover:text-[#E63946] transition-colors py-1"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}

            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-wider text-white/80 hover:text-white transition-colors py-1"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-4 h-4" />
              {BUSINESS.phone}
            </a>

            <div className="flex items-center gap-2 pt-3 border-t border-white/10">
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-heading mr-1">
                {t.language_label}:
              </span>
              <button
                onClick={() => setLanguage("de")}
                className={`text-sm font-heading uppercase tracking-wider px-4 py-2 rounded-full ${
                  language === "de"
                    ? "bg-[#E63946] text-white"
                    : "bg-white/5 text-white/70"
                }`}
                data-testid="mobile-lang-de-btn"
              >
                DE
              </button>
              <button
                onClick={() => setLanguage("ku")}
                className={`text-sm font-heading uppercase tracking-wider px-4 py-2 rounded-full ${
                  language === "ku"
                    ? "bg-[#E63946] text-white"
                    : "bg-white/5 text-white/70"
                }`}
                data-testid="mobile-lang-ku-btn"
              >
                KU
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
